import {
  memo,
  useId,
  useMemo,
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ListTodo } from "lucide-react";
import { Button } from "@primitives/Button";
import { NextButton } from "./NextButton";
import { ContentSwap } from "@primitives/ContentSwap";
import { motionStyle } from "@primitives/motionStyle";
import { Text } from "@primitives/Text";
import { useViewport } from "@primitives/viewport";
import {
  color,
  layout,
  motion as motionTokens,
  radius,
  space,
  useListItemMotion,
  usePrefersReducedMotion,
} from "@tokens/index";
import type { WorkItem } from "../../experience-shell/workflows";
import type {
  CompletedWorkItemPageSize,
  QueueWorkItemView,
} from "../../experience-shell/workitems";
import { ListEmptyLine, ListError, ListSkeleton } from "./queue-surfaces/ListStates";
import { leadingSlot } from "./queue-surfaces/listSurface";
import rowStyles from "./QueueWorkItems.module.css";
import { WorkItemRowContent } from "./queue-surfaces/WorkItemRowContent";
import { workItemTimeFingerprint } from "./queue-surfaces/workItemTime";
import { staleDataStyle } from "./staleData";
import { useNow } from "./useNow";

export { WorkItemRowContent } from "./queue-surfaces/WorkItemRowContent";
export type { WorkItemRowContentProps } from "./queue-surfaces/WorkItemRowContent";

// ---------------------------------------------------------------------------
// Public types
//
// QueueWorkItems is the focus column of the queue workspace: when a queue is
// selected in the rail, this panel lists the work items routed to it.
//
// Pure presentational composition. Data fetch, completion toggle, and filter
// clearing live in the experience-shell.
// ---------------------------------------------------------------------------

export interface QueueWorkItemsProps {
  readonly queueName: string;
  readonly items: readonly WorkItem[];
  readonly totalCount: number;
  /** No page on hand yet. Renders the skeleton. Never true during a refetch. */
  readonly isLoading: boolean;
  /** `items` are the previous queue or page while the next one loads. Dims the list. */
  readonly isStale?: boolean;
  readonly error?: Error | null;
  readonly view: QueueWorkItemView;
  readonly onToggleCompleted: () => void;
  readonly skip: number;
  readonly take: CompletedWorkItemPageSize;
  readonly onPageChange: (skip: number) => void;
  readonly onPageSizeChange: (take: CompletedWorkItemPageSize) => void;
  /**
   * Click target for a row. `WorkflowLayout` navigates into the parent
   * workflow and keeps the active queue in the URL. When omitted, rows are
   * inert list items (Storybook, read-only embeds).
   */
  readonly onOpenWorkflow?: (item: WorkItem) => void;
  /**
   * Navigate to the Galileo page for the work item's entity. When provided,
   * each row renders an open-in-Galileo control.
   */
  readonly onNavigateToGalileoEntity?: (entityType: string, entityId: string) => void;
  /**
   * Per-row live-presence slot. Returns a facepile node for the item, or
   * null when nobody is present. A render prop keeps the presence hook in
   * the experience-shell.
   */
  readonly renderPresence?: (item: WorkItem) => ReactNode;
  /**
   * Hide the visible eyebrow and heading. The host sets this when its own
   * chrome already names the queue (the compact sub-header). The heading
   * stays in the DOM, visually hidden, so the section keeps its label.
   */
  readonly hideHeading?: boolean;
  /**
   * Queue-focus triage. When provided, the header shows the Next hero
   * beside the completed toggle.
   */
  readonly onNext?: () => void;
  readonly nextPending?: boolean;
}

// ---------------------------------------------------------------------------
// Style sheet
// ---------------------------------------------------------------------------

const styles = {
  root: {
    width: "100%",
    maxWidth: layout.contentMaxWidth,
    display: "flex",
    flexDirection: "column" as const,
    gap: space[5],
    boxSizing: "border-box" as const,
  },
  // The glyph sits in the same 40px slot as a row's status glyph, so the
  // heading's left edge lines up with the row titles below it.
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: space[2],
    color: color.accent.primary,
    minWidth: 0,
    flex: 1,
  },
  headerHeadingWrap: {
    display: "flex",
    flexDirection: "column" as const,
    gap: space[1],
    minWidth: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space[3],
    flexShrink: 0,
  },
} as const;

function headerStyle(inline: boolean): CSSProperties {
  return {
    display: "flex",
    flexDirection: inline ? "row" : "column",
    alignItems: inline ? "center" : "stretch",
    justifyContent: "space-between",
    gap: space[3],
    // Matches the list's 1px hairline plus the row's inline padding.
    paddingInline: `calc(${space[2]} + 1px)`,
    minWidth: 0,
  };
}

const SKELETON_ROW_COUNT = 4;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function QueueWorkItems({
  queueName,
  items,
  totalCount,
  isLoading,
  isStale = false,
  error,
  view,
  onToggleCompleted,
  skip,
  take,
  onPageChange,
  onPageSizeChange,
  onOpenWorkflow,
  onNavigateToGalileoEntity,
  renderPresence,
  hideHeading = false,
  onNext,
  nextPending = false,
}: QueueWorkItemsProps): ReactElement {
  const headingId = useId();
  const { isAtLeast } = useViewport();
  // Title on one line with count and toggle on `expanded`+. Below that the
  // title takes line one and the count and toggle share line two.
  const inlineHeader = isAtLeast("expanded");
  const isCompletedView = view === "completed";

  // The "as of" instant advances every 30s so relative labels and the
  // Pending -> Open due boundary stay live without a refetch. Rows are
  // memoized on their time labels, so a tick re-renders only rows whose
  // label changes.
  const now = useNow(30_000);

  const countLabel = useMemo(() => {
    const noun = totalCount === 1 ? "item" : "items";
    const qualifier = isCompletedView ? "completed" : "active";
    return `${String(totalCount)} ${qualifier} ${noun}`;
  }, [totalCount, isCompletedView]);

  return (
    <section style={styles.root} aria-labelledby={headingId}>
      <header style={headerStyle(inlineHeader)} data-layout={inlineHeader ? "inline" : "stacked"}>
        {hideHeading ? (
          <h2 id={headingId} data-visually-hidden="">
            {queueName}
          </h2>
        ) : (
          <div style={styles.headerTitle}>
            <span style={leadingSlot} aria-hidden="true">
              <ListTodo size={18} strokeWidth={1.75} />
            </span>
            <div style={styles.headerHeadingWrap}>
              <Text role="eyebrow" tone="muted" tracking="caps-medium">
                Queue focus
              </Text>
              <Text as="h2" role="heading" id={headingId} truncate>
                {queueName}
              </Text>
            </div>
          </div>
        )}
        <div style={styles.headerActions}>
          <Text role="caption" tone="subtle" tabular truncate>
            {!isLoading && totalCount > 0 ? countLabel : ""}
          </Text>
          <Button
            variant={isCompletedView ? "soft" : "ghost"}
            size="sm"
            aria-pressed={isCompletedView}
            onClick={onToggleCompleted}
          >
            {isCompletedView ? "Show active" : "Show completed"}
          </Button>
          {onNext !== undefined && <NextButton onNext={onNext} pending={nextPending} />}
        </div>
      </header>

      {error !== null && error !== undefined ? (
        <ListError>Couldn&rsquo;t load work items. {error.message}</ListError>
      ) : (
        <ContentSwap
          ready={!isLoading || items.length > 0}
          skeleton={
            <div className={rowStyles.list}>
              <ListSkeleton
                label="Loading work items"
                count={SKELETON_ROW_COUNT}
                material="transparent"
                leading={18}
                twoLine
                trailing={0}
              />
            </div>
          }
        >
          {items.length === 0 ? (
            <ListEmptyLine>
              {isCompletedView
                ? "No completed work items in this queue."
                : "No active work items in this queue."}
            </ListEmptyLine>
          ) : (
            <div
              role="list"
              aria-labelledby={headingId}
              className={rowStyles.list}
              style={staleDataStyle(isStale)}
            >
              <AnimatePresence initial={false}>
                {items.map((item, index) => (
                  <WorkItemRow
                    key={item.id}
                    item={item}
                    index={index}
                    now={now}
                    {...(onOpenWorkflow !== undefined ? { onOpenWorkflow } : {})}
                    onNavigateToGalileoEntity={onNavigateToGalileoEntity}
                    renderPresence={renderPresence}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </ContentSwap>
      )}
      {isCompletedView && totalCount > 0 && (
        <QueueWorkItemsPager
          skip={skip}
          take={take}
          itemCount={items.length}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface WorkItemRowProps {
  readonly item: WorkItem;
  readonly index: number;
  readonly now: Date;
  readonly onOpenWorkflow?: (item: WorkItem) => void;
  readonly onNavigateToGalileoEntity: ((entityType: string, entityId: string) => void) | undefined;
  readonly renderPresence: ((item: WorkItem) => ReactNode) | undefined;
}

// A clock tick changes `now` for every row; only rows whose time labels
// change re-render. Everything else is compared by reference, so the host
// must pass stable callbacks.
function workItemRowPropsEqual(prev: WorkItemRowProps, next: WorkItemRowProps): boolean {
  return (
    prev.item === next.item &&
    prev.index === next.index &&
    prev.onOpenWorkflow === next.onOpenWorkflow &&
    prev.onNavigateToGalileoEntity === next.onNavigateToGalileoEntity &&
    prev.renderPresence === next.renderPresence &&
    (prev.now === next.now ||
      workItemTimeFingerprint(prev.item, prev.now) === workItemTimeFingerprint(next.item, next.now))
  );
}

const WorkItemRow = memo(function WorkItemRow({
  item,
  index,
  now,
  onOpenWorkflow,
  onNavigateToGalileoEntity,
  renderPresence,
}: WorkItemRowProps): ReactElement {
  const arrival = useListItemMotion(index, false);
  const reduced = usePrefersReducedMotion();
  const canOpen =
    onOpenWorkflow !== undefined && item.entityType.length > 0 && item.entityId.length > 0;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!canOpen) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpenWorkflow(item);
    }
  };

  const interactiveProps = canOpen
    ? {
        role: "button" as const,
        tabIndex: 0,
        "aria-label": `Open workflow for ${item.body.slice(0, 80)}`,
        onClick: () => onOpenWorkflow(item),
        onKeyDown: handleKeyDown,
      }
    : { role: "listitem" as const };

  const pressMotion =
    canOpen && !reduced
      ? {
          whileTap: {
            scale: motionTokens.press.scale,
            transition: { duration: motionTokens.press.duration },
          },
        }
      : {};

  return (
    <motion.div
      {...interactiveProps}
      className={rowStyles.row}
      data-hoverable={canOpen || undefined}
      style={motionStyle({ cursor: canOpen ? "pointer" : "default" })}
      initial={arrival.initial}
      animate={arrival.animate}
      transition={arrival.transition}
      {...pressMotion}
    >
      <WorkItemRowContent
        item={item}
        now={now}
        onNavigateToGalileoEntity={onNavigateToGalileoEntity}
        renderPresence={renderPresence}
        dense
        {...(rowStyles.action !== undefined ? { actionClassName: rowStyles.action } : {})}
      />
    </motion.div>
  );
}, workItemRowPropsEqual);

// ---------------------------------------------------------------------------
// Pager
// ---------------------------------------------------------------------------

export interface QueueWorkItemsPagerProps {
  readonly skip: number;
  readonly take: CompletedWorkItemPageSize;
  readonly itemCount: number;
  readonly totalCount: number;
  readonly onPageChange: (skip: number) => void;
  readonly onPageSizeChange: (take: CompletedWorkItemPageSize) => void;
}

export function QueueWorkItemsPager({
  skip,
  take,
  itemCount,
  totalCount,
  onPageChange,
  onPageSizeChange,
}: QueueWorkItemsPagerProps): ReactElement {
  const first = Math.min(skip + 1, totalCount);
  const last = Math.min(skip + itemCount, totalCount);
  const hasPrevious = skip > 0;
  const hasNext = skip + itemCount < totalCount;

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const value = Number(event.currentTarget.value);
    if (value === 20 || value === 50 || value === 100) {
      onPageSizeChange(value);
    }
  };

  return (
    <nav
      aria-label="Completed work item pages"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: space[3],
        flexWrap: "wrap",
        borderTop: `1px solid ${color.border.subtle}`,
        paddingTop: space[3],
      }}
    >
      <Text role="caption" tone="subtle" tabular>
        {first}–{last} of {totalCount}
      </Text>
      <div style={{ display: "flex", alignItems: "center", gap: space[2], flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: space[2] }}>
          <Text as="span" role="caption" tone="muted">
            Per page
          </Text>
          <select
            aria-label="Completed items per page"
            value={take}
            onChange={handlePageSizeChange}
            style={{
              height: 40,
              paddingInline: space[2],
              borderRadius: radius.md,
              border: `1px solid ${color.border.subtle}`,
              background: color.bg.surface,
              color: color.fg.default,
            }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <Button
          variant="ghost"
          size="sm"
          disabled={!hasPrevious}
          onClick={() => onPageChange(Math.max(0, skip - take))}
        >
          Previous
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!hasNext}
          onClick={() => onPageChange(skip + take)}
        >
          Next
        </Button>
      </div>
    </nav>
  );
}
